import {
  Card,
  CardCloseButton,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@components/ui/card";
import Spinner from "@components/ui/spinner";
import { cn } from "@lib/cn";
import { useAppDispatch } from "@store";
import { closeDialog } from "@store/dialogs";
import { toast } from "react-toastify";
import Button from "@/components/ui/button";
import {
  useDeleteFolderMutation,
  useGetPagesCountQuery,
} from "@/shared/services";
import type { IStoriesFolder } from "@/shared/types";

const DIALOG_ID = "stories-lib-folder-delete";

interface IProps {
  folder: IStoriesFolder;
}

export const DeleteFolderDialog = ({ folder }: IProps) => {
  const dispatch = useAppDispatch();

  const { mutateAsync: deleteFolder, isPending: isLoading } =
    useDeleteFolderMutation();
  const { data: pagesCount, isFetching: isLoadingPagesCount } =
    useGetPagesCountQuery({
      status: "Live,Draft",
      folder: folder._id,
    });

  const handleClose = () => {
    dispatch(closeDialog(DIALOG_ID));
  };

  const handleSubmit = async () => {
    if (isLoading || isLoadingPagesCount) return;

    try {
      await deleteFolder(folder._id);
    } catch {
      toast.error("Error deleting folder");
    }

    handleClose();
  };

  const canDelete = pagesCount && pagesCount.count === 0;
  return (
    <Card className="tw-w-[468px] tw-gap-2">
      <CardHeader className="tw-flex tw-items-center tw-justify-between">
        <CardTitle className="tw-flex">
          Delete "
          <span className="tw-block tw-max-w-56 tw-truncate">
            {folder.name}
          </span>
          " folder
        </CardTitle>
        <CardCloseButton onClick={handleClose} />
      </CardHeader>
      <CardContent className="tw-mb-6">
        {isLoadingPagesCount || (pagesCount && pagesCount.count === 0) ? (
          <p className="tw-mb-0 tw-font-sans tw-text-sm">
            This action can’t be undone. Are you sure you want to delete it?
          </p>
        ) : (
          <p className="tw-mb-0 tw-font-sans tw-text-sm">
            To delete this folder you must archive all its stories first.
          </p>
        )}
      </CardContent>
      <CardFooter className="tw-justify-end tw-gap-4">
        <Button
          variant="secondary"
          onClick={handleClose}
          className="tw-font-sans tw-text-sm tw-font-medium"
        >
          Go back
        </Button>
        {canDelete && (
          <Button
            variant="destructive"
            onClick={handleSubmit}
            disabled={isLoadingPagesCount}
            className={cn(
              "tw-font-sans tw-text-sm tw-font-medium",
              isLoading && "tw-cursor-not-allowed",
            )}
          >
            {isLoading ? (
              <>
                <Spinner className="tw-mr-2 tw-size-3 tw-border-white" />
                Deleting...
              </>
            ) : (
              "Yes, delete"
            )}
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

DeleteFolderDialog.id = DIALOG_ID;
